import { Loading } from '@marketing-milk/frontend'
import { Auth0UserWithRoles, UserWithBusiness } from '@marketing-milk/interfaces'
import { Button, Divider, TextField, Typography } from '@material-ui/core'
import ScheduleIcon from '@material-ui/icons/Schedule'
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser'
import { Alert } from '@material-ui/lab'
import { format, formatDistanceToNow } from 'date-fns'
import { capitalize } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Delete } from '../../modals/Delete'
import { userService } from '../../../../../app/service/user'

export interface DetailsTabType {
  auth0User?: Auth0UserWithRoles
  mmUser: UserWithBusiness
  isLoading?: boolean
  refetchUser?: () => void
}

export function DetailsTab({ auth0User, mmUser, isLoading, refetchUser }: DetailsTabType) {
  const { name, email, created_at, identities, last_login } = auth0User ?? {}
  const history = useHistory()
  const [open, setOpen] = useState(false)
  const [firstName, setFirstName] = useState(mmUser?.firstName ?? '')
  const [lastName, setLastName] = useState(mmUser?.lastName ?? '')
  const [notificationEmail, setNotificationEmail] = useState(mmUser?.notificationEmail ?? '')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (mmUser) {
      setFirstName(mmUser.firstName ?? '')
      setLastName(mmUser.lastName ?? '')
      setNotificationEmail(mmUser.notificationEmail ?? '')
    }
  }, [mmUser])

  const hasChanges =
    firstName !== (mmUser?.firstName ?? '') ||
    lastName !== (mmUser?.lastName ?? '') ||
    notificationEmail !== (mmUser?.notificationEmail ?? '')

  const handleSave = async () => {
    if (!mmUser) return

    setIsSaving(true)
    try {
      await userService.updateByID(mmUser.id, {
        firstName,
        lastName,
        notificationEmail,
      })
      toast.success('User details updated successfully.')
      refetchUser?.()
    } catch (e) {
      toast.error('Failed to update user details. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const clickDelete = async (user: UserWithBusiness | undefined) => {
    if (user) {
      try {
        await userService.deleteByID(user?.id)
        history.push('/admin/users/list')
        toast.success('Successfully deleted user from Marketing Milk.')
      } catch (e) {
        toast.error(
          'There was a problem deleting this user. Please try again later. If the problem persists contact support.'
        )
      }
    }
  }

  if (isLoading) return <Loading />

  return (
    <div className="container-fluid p-0">
      <div
        className="container-fluid"
        style={{
          padding: '10px',
          border: '1px solid lightgrey',
          borderRadius: '5px',
        }}
      >
        <div className="row px-4 pt-4">
          <div className="col-sm-4 col-xs-6 pb-4">
            <TextField
              label="First Name"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
            />
          </div>
          <div className="col-sm-4 col-xs-6 pb-4">
            <TextField
              label="Last Name"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
            />
          </div>
          <div className="col-sm-4 col-xs-6 pb-4">
            <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <Typography style={{ fontWeight: 'bold' }} gutterBottom>
                Email
              </Typography>
              <Typography>{mmUser ? mmUser.email : email}</Typography>
            </div>
          </div>
          <div className="col-sm-4 col-xs-6 pb-4">
            <TextField
              label="Notification Email"
              value={notificationEmail}
              onChange={e => setNotificationEmail(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              type="email"
            />
          </div>
          <div className="col-sm-4 col-xs-6 pb-4">
            <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <Typography style={{ fontWeight: 'bold' }} gutterBottom>
                Signed Up
              </Typography>
              <Typography>
                {created_at
                  ? `${format(new Date(created_at), 'PP')} (${formatDistanceToNow(
                      new Date(created_at),
                      { addSuffix: true }
                    )})`
                  : 'Pending'}
              </Typography>
            </div>
          </div>
          <div className="col-sm-4 col-xs-6 pb-4">
            <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <Typography style={{ fontWeight: 'bold' }} gutterBottom>
                Connection
              </Typography>
              <Typography>
                {identities
                  ? identities[0].provider === 'auth0'
                    ? 'Email/Password'
                    : capitalize(identities[0].provider)
                  : 'N/A'}
              </Typography>
            </div>
          </div>
          <div className="col-sm-4 col-xs-6 pb-4">
            <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <Typography style={{ fontWeight: 'bold' }} gutterBottom>
                Latest Login
              </Typography>
              <Typography>
                {last_login
                  ? `${format(new Date(last_login), 'PP')} (${formatDistanceToNow(
                      new Date(last_login),
                      { addSuffix: true }
                    )})`
                  : 'N/A'}
              </Typography>
            </div>
          </div>
          <div className="col-sm-4 col-xs-6 pb-4">
            <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <Typography style={{ fontWeight: 'bold' }} gutterBottom>
                Accepted Invite
              </Typography>
              <Typography>
                {mmUser?.acceptedInvite ? <VerifiedUserIcon /> : <ScheduleIcon />}
              </Typography>
            </div>
          </div>
          {hasChanges && (
            <div className="col-12 pt-2">
              <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="row px-4 pt-5">
        <Typography variant="h4" className="mb-3">
          Danger Zone
        </Typography>

        <Divider />
      </div>

      <div className="row px-4">
        <Alert
          variant="standard"
          severity="error"
          className="mt-3 w-100"
          action={
            <Button
              className="bg-white"
              disableElevation
              data-testid="delete-user-btn"
              variant="contained"
              onClick={() => setOpen(true)}
            >
              Delete
            </Button>
          }
        >
          <Typography variant="h6" className="mt-n1">
            Delete User
          </Typography>
          Warning: Deleting this user willl remove the user from our system and their access to
          Marketing Milk will be revoked.
        </Alert>
      </div>

      {open && (
        <Delete
          title={<Typography style={{ fontSize: 20 }}>Remove User Account?</Typography>}
          body={
            <>
              <Typography style={{ fontSize: 15, paddingBottom: 15 }}>
                You are about to <span className="text-danger font-weight-bold">delete</span> the
                user account for
                <span
                  style={{ fontWeight: 'bold' }}
                >{` ${mmUser?.firstName} ${mmUser?.lastName}.`}</span>
              </Typography>

              <Typography variant="subtitle1">
                Upon clicking "Delete", their account will be deleted and their access to Marketing
                Milk will be revoked.
              </Typography>
            </>
          }
          onClose={() => {
            setOpen(false)
          }}
          withButton={false}
          onSubmitted={clickDelete}
          item={mmUser}
          openModal={open}
          maxWidth={'sm'}
        />
      )}
    </div>
  )
}
